/**
 * The spreadsheet import — preview then commit.
 *
 * Not GraphQL, for the reason `files.js` and the CSV export are not: an upload
 * is a multipart body, which GraphQL cannot express.
 *
 * THE FILE IS SENT TWICE, once to preview and once to commit, and that is
 * deliberate. Parking it on the server between the two would need a session,
 * an expiry and a cleanup job for a file the browser is already holding — and
 * the commit re-runs the same planner anyway, so the second answer is the one
 * that counts.
 */
import { getAccessToken } from './auth.jsx';
import { BFF_ORIGIN } from './runtimeEnv.js';

const BASE = BFF_ORIGIN;

export class ImportError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'ImportError';
    this.code = code ?? 'INTERNAL_SERVER_ERROR';
  }
}

/**
 * Send the workbook. `mode` is 'preview' (writes nothing) or 'commit'.
 *
 * Returns { created, matched, skipped, invalid, newSources, rows, columns,
 * sheet, sheets } — the same shape either way, because the preview IS the
 * plan the commit applies.
 */
export async function importContacts(file, { mode = 'preview', sheet, rows, signal } = {}) {
  const token = getAccessToken();
  const form = new FormData();
  form.append('file', file);
  // Absent means every row. A commit carries the subset the operator ticked,
  // and the server RE-PLANS it — dropping one row can change another's
  // outcome, and only the planner knows that.
  if (rows) form.append('rows', rows.join(','));

  const params = new URLSearchParams({ mode });
  if (sheet) params.set('sheet', sheet);

  const response = await fetch(`${BASE}/imports/contacts?${params}`, {
    method: 'POST',
    // No content-type header: the browser sets it WITH the multipart boundary,
    // and setting it by hand produces a body the server cannot split.
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: form,
    signal,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ImportError(
      payload?.error?.message ?? `import failed (${response.status})`,
      payload?.error?.code,
    );
  }
  return payload;
}
